import { useState } from 'react';
import {
  Combobox,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions
} from '@headlessui/react';
import FormField from '@ramsey-design-system/form-field';
import Input from '@ramsey-design-system/input';
import { SearchIcon } from '@ramsey-design-system/icons';
import { useWizard } from 'react-use-wizard';
import { useMatcherContext } from '../context';
import { getAnsweredString, IndustryOption } from '../flow/types';
import {
  filterIndustries,
  findExactIndustryMatch
} from '../flow/filterIndustries';
import {
  INDUSTRY_OPTIONS,
  OTHER_INDUSTRY_OPTION
} from '../flow/industryOptions';
import { trackStepCompleted } from '../../../shared/analytics';
import {
  getMatcherFormName,
  useMatcherFieldSize,
  useTrackMatcherStepStarted
} from '../utils';
import {
  MatcherStandardNextButton,
  MatcherStandardStepTitle,
  MatcherStepWrapper
} from '../components';

const STEP_NAME = 'matcher_industry_step';
const STEP_LABEL = 'What industry are you in?';
const SUGGESTION_CLASS_NAME =
  'cursor-pointer p-3 text-2 data-[focus]:bg-blue-10 data-[selected]:font-semibold';

// Label echoes the fan's query for context, but the code is always the fixed OTHER sentinel -
// we never persist arbitrary typed text as an answer.
function buildOtherOption(query: string): IndustryOption {
  const trimmedQuery = query.trim();
  return {
    code: OTHER_INDUSTRY_OPTION.code,
    label: trimmedQuery
      ? `Other: "${trimmedQuery}"`
      : OTHER_INDUSTRY_OPTION.label
  };
}

function MatcherIndustryStep() {
  const { nextStep, activeStep } = useWizard();
  const { matcherAnswers, updateMatcherAnswer } = useMatcherContext();
  const fieldSize = useMatcherFieldSize();
  const [query, setQuery] = useState('');
  // Rehydrates the fan's prior pick so revisiting this step shows a label, not the raw stored
  // code. No typed text to recover for a prior OTHER answer, by design - see buildOtherOption.
  const [selected, setSelected] = useState<IndustryOption | undefined>(() => {
    const answeredCode = getAnsweredString(matcherAnswers.industry);
    if (!answeredCode) return undefined;
    if (answeredCode === OTHER_INDUSTRY_OPTION.code)
      return OTHER_INDUSTRY_OPTION;
    return INDUSTRY_OPTIONS.find((option) => option.code === answeredCode);
  });

  const suggestions = filterIndustries(query);
  const otherOption = buildOtherOption(query);

  useTrackMatcherStepStarted(activeStep + 1, STEP_NAME, STEP_LABEL);

  async function trackAndAdvance(value: string) {
    trackStepCompleted({
      step: activeStep + 1,
      step_name: STEP_NAME,
      step_label: STEP_LABEL,
      answer_labels: ['Search Your Industry'],
      answer_values: [value],
      form_name: getMatcherFormName()
    });
    await nextStep();
  }

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    // Explicit pick wins, then an exact label/keyword match; anything else falls back to the
    // fixed OTHER sentinel rather than being accepted as free text.
    const value =
      selected?.code ??
      findExactIndustryMatch(query)?.code ??
      OTHER_INDUSTRY_OPTION.code;

    updateMatcherAnswer('industry', { status: 'answered', value });
    await trackAndAdvance(value);
  }

  async function onSkip() {
    updateMatcherAnswer('industry', { status: 'skipped' });
    await trackAndAdvance('skip');
  }

  return (
    <MatcherStepWrapper onSkip={onSkip}>
      <form onSubmit={onSubmit} className="flex-1 flex flex-col">
        <MatcherStandardStepTitle noSubtitle>
          {STEP_LABEL}
        </MatcherStandardStepTitle>

        <div className="mb-4 relative">
          <Combobox<IndustryOption>
            immediate
            by="code"
            value={selected}
            onChange={(value) => setSelected(value ?? undefined)}
            // onClose fires on any close, not just a pick - only clear the query once something
            // is actually selected, or a stray blur silently wipes what the fan typed.
            onClose={() => {
              if (selected) setQuery('');
            }}
          >
            <FormField
              id="industry_search"
              label="Search Your Industry"
              size={fieldSize}
            >
              {() => (
                <ComboboxInput
                  as={Input}
                  size={fieldSize}
                  id="industry_search"
                  data-testid="matcher_industry_search_input"
                  icon={SearchIcon}
                  // RDS Input forwards className to its wrapper div, not the <input> - style is
                  // the only way from here to nudge text off the icon without shifting the icon
                  // itself. Adds a flat +4px on top of RDS's own icon padding (32px md/36px lg).
                  style={{ paddingLeft: fieldSize === 'lg' ? 40 : 36 }}
                  placeholder="e.g. construction"
                  autoComplete="off"
                  displayValue={(option: IndustryOption | undefined) =>
                    option?.label ?? query
                  }
                  onChange={(event) => setQuery(event.target.value)}
                />
              )}
            </FormField>
            <ComboboxOptions
              portal={false}
              className="absolute inset-x-0 top-full z-10 mt-1 overflow-hidden rounded-md border border-gray-60 bg-white divide-y divide-gray-30 empty:invisible"
            >
              {suggestions.map((option) => (
                <ComboboxOption
                  key={option.code}
                  value={option}
                  className={SUGGESTION_CLASS_NAME}
                >
                  {option.label}
                </ComboboxOption>
              ))}
              <ComboboxOption
                value={otherOption}
                className={SUGGESTION_CLASS_NAME}
              >
                {otherOption.label}
              </ComboboxOption>
            </ComboboxOptions>
          </Combobox>
        </div>

        <div className="mt-auto md:mt-0">
          <MatcherStandardNextButton />
        </div>
      </form>
    </MatcherStepWrapper>
  );
}

export { MatcherIndustryStep };
